import React, { useState } from 'react'
import {
    Modal,
    Text,
    Group,
    Button,
    Grid,
    Card,
    Image,
    Badge,
    Loader,
    Center,
    Alert,
    Select,
    Stack,
    ActionIcon,
    Flex,
    Box,
    Tooltip
} from '@mantine/core'
import {
    IconAlertCircle,
    IconCheck,
    IconX,
    IconFilter,
    IconRefresh
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { gamePlatformImagesApi } from '../services/api'
import type { GamePlatformImage } from '../types'

interface ImageBrowserModalProps {
    opened: boolean
    onClose: () => void
    onSelect: (imageUrl: string) => void
    gameId: string
    platformId: string
    title?: string
}

export const ImageBrowserModal: React.FC<ImageBrowserModalProps> = ({
    opened,
    onClose,
    onSelect,
    gameId,
    platformId,
    title = 'Select Image'
}) => {
    const [selectedType, setSelectedType] = useState<string>('')
    const [selectedImage, setSelectedImage] = useState<GamePlatformImage | null>(null)

    const { data: images, isLoading, error, refetch } = useQuery({
        queryKey: ['game-platform-images', gameId, platformId, selectedType],
        queryFn: () => gamePlatformImagesApi.getGamePlatformImages(gameId, platformId, selectedType ? { type: selectedType } : undefined),
        enabled: opened && !!gameId && !!platformId,
        select: (response) => response.data.data
    })

    const handleSelectImage = () => {
        if (selectedImage) {
            onSelect(selectedImage.url)
            onClose()
            setSelectedImage(null)
        }
    }

    const handleClose = () => {
        onClose()
        setSelectedImage(null)
        setSelectedType('')
    }

    const imageTypes = [
        { value: '', label: 'All Types' },
        { value: 'COVER', label: 'Cover' },
        { value: 'SCREENSHOT', label: 'Screenshot' },
        { value: 'BANNER', label: 'Banner' },
        { value: 'THUMBNAIL', label: 'Thumbnail' },
        { value: 'ARTWORK', label: 'Artwork' }
    ]

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={title}
            size="xl"
            centered
        >
            <Stack gap="md">
                {/* Filters */}
                <Group justify="space-between" wrap="wrap" gap="md">
                    <Group gap="sm">
                        <Select
                            label="Image Type"
                            placeholder="Filter by type"
                            data={imageTypes}
                            value={selectedType}
                            onChange={(value) => setSelectedType(value || '')}
                            leftSection={<IconFilter size={16} />}
                            w={200}
                        />
                    </Group>
                    <Button
                        variant="light"
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => refetch()}
                        loading={isLoading}
                    >
                        Refresh
                    </Button>
                </Group>

                {/* Loading State */}
                {isLoading && (
                    <Center py="xl">
                        <Loader size="lg" />
                    </Center>
                )}

                {/* Error State */}
                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
                        Failed to load images. Please try again.
                    </Alert>
                )}

                {/* Images Grid */}
                {!isLoading && !error && images && (
                    <>
                        {images.length === 0 ? (
                            <Center py="xl">
                                <Text c="dimmed">No images found for this game platform</Text>
                            </Center>
                        ) : (
                            <>
                                <Grid>
                                    {images.map((image: GamePlatformImage) => (
                                        <Grid.Col key={image.id} span={{ base: 6, md: 4, lg: 3 }}>
                                            <Card
                                                withBorder
                                                p="xs"
                                                style={{
                                                    cursor: 'pointer',
                                                    border: selectedImage?.id === image.id ? '2px solid var(--mantine-color-blue-6)' : undefined,
                                                    backgroundColor: selectedImage?.id === image.id ? 'var(--mantine-color-blue-0)' : undefined
                                                }}
                                                onClick={() => setSelectedImage(image)}
                                            >
                                                <Stack gap="xs">
                                                    <Tooltip
                                                        label={
                                                            <Image
                                                                src={image.url}
                                                                alt={`${image.type} image preview`}
                                                                w={500}
                                                                h={400}
                                                                fit="contain"
                                                                style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
                                                                fallbackSrc="data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M0 0h100v100H0z' fill='%23f1f3f5'/%3e%3cpath d='M50 50m-20 0a20 20 0 1 1 40 0a20 20 0 1 1-40 0' stroke='%23adb5bd' stroke-width='2' fill='none'/%3e%3c/svg%3e"
                                                            />
                                                        }
                                                        withArrow
                                                        position="top"
                                                        w={500}
                                                    >
                                                        <Image
                                                            src={image.url}
                                                            alt={`${image.type} image`}
                                                            h={180}
                                                            fit="contain"
                                                            fallbackSrc="data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M0 0h100v100H0z' fill='%23f1f3f5'/%3e%3cpath d='M50 50m-20 0a20 20 0 1 1 40 0a20 20 0 1 1-40 0' stroke='%23adb5bd' stroke-width='2' fill='none'/%3e%3c/svg%3e"
                                                        />
                                                    </Tooltip>
                                                    <Box>
                                                        <Badge size="xs" variant="light" mb={4}>
                                                            {image.type}
                                                        </Badge>
                                                        <Text size="xs" c="dimmed" lineClamp={1}>
                                                            {image.store.name}
                                                        </Text>
                                                    </Box>
                                                </Stack>
                                            </Card>
                                        </Grid.Col>
                                    ))}
                                </Grid>

                                {/* Selection Info */}
                                {selectedImage && (
                                    <Alert color="blue" variant="light">
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text size="sm" fw={500}>
                                                    Selected: {selectedImage.type} from {selectedImage.store.name}
                                                </Text>
                                                <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
                                                    {selectedImage.url}
                                                </Text>
                                            </Box>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                onClick={() => setSelectedImage(null)}
                                            >
                                                <IconX size={16} />
                                            </ActionIcon>
                                        </Flex>
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* Actions */}
                <Group justify="flex-end" gap="sm">
                    <Button variant="light" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        leftSection={<IconCheck size={16} />}
                        onClick={handleSelectImage}
                        disabled={!selectedImage}
                    >
                        Select Image
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
} 